/**
 * G21 Brand Marks – editorial typography in inline SVG.
 * Uses currentColor so it adapts to navbar light/dark contexts.
 *
 * Variants:
 *   - <G21Wordmark size={...} />   horizontal lockup for navbar/footer (G21 + WIESBADEN tagline)
 *   - <G21Stacked  size={...} />   stacked variant for footer / larger spaces
 *   - <G21Mark     size={...} />   minimal "G21" mark for favicons / small tiles
 */

export const G21Wordmark = ({ size = 32, className = "", testid }) => (
    <svg
        viewBox="0 0 230 50"
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="G21 – Neubau Wiesbaden-Biebrich"
        className={className}
        data-testid={testid}
    >
        {/* Subtle top rule */}
        <line x1="0" y1="2" x2="230" y2="2" stroke="currentColor" strokeWidth="0.6" opacity="0.35" />

        {/* "G" – Playfair italic, refined serif */}
        <text
            x="0" y="38"
            fontFamily="'Playfair Display', Georgia, serif"
            fontSize="38"
            fontWeight="500"
            fontStyle="italic"
            fill="currentColor"
            letterSpacing="-1.5"
        >
            G
        </text>

        {/* Hairline vertical separator */}
        <line x1="29" y1="14" x2="29" y2="40" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />

        {/* "21" – Playfair upright, slightly smaller */}
        <text
            x="34" y="38"
            fontFamily="'Playfair Display', Georgia, serif"
            fontSize="32"
            fontWeight="600"
            fill="currentColor"
            letterSpacing="-1"
        >
            21
        </text>

        {/* Right wordmark block */}
        <text
            x="84" y="22"
            fontFamily="'Outfit', system-ui, sans-serif"
            fontSize="9"
            fontWeight="700"
            fill="currentColor"
            letterSpacing="3"
        >
            NEUBAU
        </text>
        <text
            x="84" y="34"
            fontFamily="'Outfit', system-ui, sans-serif"
            fontSize="8"
            fontWeight="500"
            fill="currentColor"
            letterSpacing="2"
            opacity="0.7"
        >
            WIESBADEN · BIEBRICH
        </text>

        {/* Bottom rule */}
        <line x1="0" y1="46" x2="230" y2="46" stroke="currentColor" strokeWidth="0.4" opacity="0.25" />
    </svg>
);

export const G21Stacked = ({ size = 90, className = "" }) => (
    <svg
        viewBox="0 0 160 100"
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="G21 Wiesbaden-Biebrich"
        className={className}
    >
        {/* Top rule with small mark */}
        <line x1="20" y1="8" x2="60" y2="8" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
        <line x1="100" y1="8" x2="140" y2="8" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
        <text
            x="80" y="11" textAnchor="middle"
            fontFamily="'Outfit', system-ui, sans-serif"
            fontSize="6.5" fontWeight="700"
            fill="currentColor" opacity="0.5" letterSpacing="3.5"
        >
            EST · 2026
        </text>

        {/* Big "G21" – mixed serif italic + upright */}
        <text
            x="80" y="62" textAnchor="middle"
            fontFamily="'Playfair Display', Georgia, serif"
            fontSize="56" fontWeight="500"
            fill="currentColor"
            letterSpacing="-2"
        >
            <tspan fontStyle="italic">G</tspan><tspan fontWeight="600">21</tspan>
        </text>

        {/* Tag line */}
        <text
            x="80" y="80" textAnchor="middle"
            fontFamily="'Outfit', system-ui, sans-serif"
            fontSize="8" fontWeight="600"
            fill="currentColor" letterSpacing="3"
        >
            NEUBAU WIESBADEN
        </text>
        <text
            x="80" y="92" textAnchor="middle"
            fontFamily="'Outfit', system-ui, sans-serif"
            fontSize="7" fontWeight="500"
            fill="currentColor" opacity="0.65" letterSpacing="2.5"
        >
            BIEBRICH · GAUGASSE 21
        </text>
    </svg>
);

export const G21Mark = ({ size = 40, className = "" }) => (
    <svg
        viewBox="0 0 80 80"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <rect x="2" y="2" width="76" height="76" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <line x1="14" y1="14" x2="66" y2="14" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
        <text
            x="40" y="56" textAnchor="middle"
            fontFamily="'Playfair Display', Georgia, serif"
            fontSize="36" fontWeight="500"
            fill="currentColor"
            letterSpacing="-1"
        >
            <tspan fontStyle="italic">G</tspan><tspan fontWeight="600">21</tspan>
        </text>
        <line x1="14" y1="66" x2="66" y2="66" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
    </svg>
);
